// frontend/src/components/widgets/RSSWidget.js - OPTIMIZED WITH RESPONSIVE + CARD IMAGE FIX
import React, { useState, useEffect, useRef } from 'react';
import { Rss, ExternalLink, Calendar, User, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const RSSWidget = ({ config, screenSize = 'FHD' }) => {
  const { t } = useTranslation();
  const [feedData, setFeedData] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const tickerRef = useRef(null);

  // ✅ RESPONSIVE: Get multipliers based on screen size
  const getResponsiveMultipliers = () => {
    const multipliers = {
      SM: { font: 0.4, spacing: 0.45, card: 0.5 },    // 640px - Mobile
      MD: { font: 0.6, spacing: 0.65, card: 0.7 },    // 768px - Tablet
      HD: { font: 0.8, spacing: 0.85, card: 0.85 },   // 1024px - HD
      FHD: { font: 1.0, spacing: 1.0, card: 1.0 },    // 1920px - Full HD
      QHD: { font: 1.15, spacing: 1.1, card: 1.1 },   // 2560px - 2K
      '4K': { font: 1.4, spacing: 1.3, card: 1.3 }    // 3840px+ - 4K
    };
    return multipliers[screenSize] || multipliers.FHD;
  };

  const m = getResponsiveMultipliers();

// Load RSS feed
  useEffect(() => {
    const loadFeed = async () => {
      try {
        setLoading(true);
        
        // ✅ PUBLIC endpoint (kein Token nötig für Player)
        const response = await fetch(
          `${process.env.REACT_APP_API_URL || 'https://app.alpenscreen.ch/api'}/rss/public/feed?url=${encodeURIComponent(config.feedUrl)}&maxItems=${config.maxItems || 10}`
        );

        const data = await response.json();
        if (data.success) {
          setFeedData(data.feed);
          setError(null);
        } else {
          setError(data.message || 'Failed to load feed');
        }
      } catch (err) {
        console.error('RSS Load Error:', err);
        setError('Failed to load RSS feed');
      } finally {
        setLoading(false);
      }
    };

    loadFeed();

    // Auto-refresh interval
    const interval = setInterval(loadFeed, (config.updateInterval || 600) * 1000);
    return () => clearInterval(interval);
  }, [config.feedUrl, config.maxItems, config.updateInterval]);

  // Auto-rotate items for fullscreen layout
  useEffect(() => {
    if (config.layout === 'fullscreen' && feedData && feedData.items.length > 0) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % feedData.items.length);
      }, (config.itemDuration || 10) * 1000);
      return () => clearInterval(interval);
    }
  }, [config.layout, config.itemDuration, feedData]);

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);

    if (diff < 60) return t('rss.justNow');
    if (diff < 3600) return `${Math.floor(diff / 60)}${t('rss.minAgo')}`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}${t('rss.hoursAgo')}`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}${t('rss.daysAgo')}`;
    
    return date.toLocaleDateString(undefined, { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  // Truncate text
  const truncate = (text, maxLength) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  // Get theme styles
  const getThemeStyles = () => {
    const themes = {
      light: {
        background: 'bg-gradient-to-br from-gray-50 to-gray-100',
        cardBg: 'bg-white',
        text: 'text-gray-900',
        textSecondary: 'text-gray-600',
        textTertiary: 'text-gray-500',
        border: 'border-gray-200',
        accent: 'bg-blue-600',
        accentText: 'text-blue-600',
        accentGradient: 'from-blue-500 to-blue-700',
        badge: 'bg-blue-600 text-white',
        hover: 'hover:bg-gray-50',
        shadow: 'shadow-lg shadow-gray-200/50',
        overlay: 'bg-white/95 backdrop-blur-sm',
        metadataBg: 'bg-gray-100',
        divider: 'bg-gradient-to-r from-blue-500 to-cyan-500'
      },
      dark: {
        background: 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900',
        cardBg: 'bg-gray-800/50',
        text: 'text-gray-100',
        textSecondary: 'text-gray-300',
        textTertiary: 'text-gray-400',
        border: 'border-gray-700',
        accent: 'bg-cyan-500',
        accentText: 'text-cyan-400',
        accentGradient: 'from-cyan-400 to-blue-500',
        badge: 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white',
        hover: 'hover:bg-gray-700/50',
        shadow: 'shadow-2xl shadow-black/50',
        overlay: 'bg-gray-900/90 backdrop-blur-md',
        metadataBg: 'bg-white/5',
        divider: 'bg-gradient-to-r from-cyan-400 to-blue-500'
      },
      colored: {
        background: 'bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700',
        cardBg: 'bg-white/10',
        text: 'text-white',
        textSecondary: 'text-white/90',
        textTertiary: 'text-white/70',
        border: 'border-white/20',
        accent: 'bg-white',
        accentText: 'text-white',
        accentGradient: 'from-white to-blue-100',
        badge: 'bg-white/20 backdrop-blur-md text-white border-2 border-white/30',
        hover: 'hover:bg-white/20',
        shadow: 'shadow-2xl shadow-black/30',
        overlay: 'bg-gradient-to-br from-blue-600/95 to-purple-700/95 backdrop-blur-sm',
        metadataBg: 'bg-white/10 backdrop-blur-md',
        divider: 'bg-gradient-to-r from-white/80 to-blue-200/80'
      },
      classic: {
        background: 'bg-blue-600',
        cardBg: 'bg-blue-700/30',
        text: 'text-white',
        textSecondary: 'text-white/90',
        textTertiary: 'text-white/70',
        border: 'border-white/20',
        accent: 'bg-white',
        accentText: 'text-white',
        accentGradient: 'from-white to-blue-100',
        badge: 'bg-white/20 backdrop-blur-sm text-white border-2 border-white/30',
        hover: 'hover:bg-white/10',
        shadow: 'shadow-2xl shadow-blue-900/50',
        overlay: 'bg-blue-600/95 backdrop-blur-sm',
        metadataBg: 'bg-white/10 backdrop-blur-sm',
        divider: 'bg-white/60'
      },
      classicBlue: {
        background: 'bg-gradient-to-br from-[#1e3c72] via-[#2a5298] to-[#1e3c72]',
        cardBg: 'bg-white/10 backdrop-blur-md',
        text: 'text-white',
        textSecondary: 'text-white/90',
        textTertiary: 'text-white/70',
        border: 'border-white/20',
        accent: 'bg-white',
        accentText: 'text-white',
        accentGradient: 'from-[#1e3c72] to-[#2a5298]',
        badge: 'bg-white/20 backdrop-blur-md text-white border-2 border-white/30',
        hover: 'hover:bg-white/15',
        shadow: 'shadow-2xl shadow-blue-900/50',
        overlay: 'bg-gradient-to-br from-[#1e3c72]/95 via-[#2a5298]/95 to-[#1e3c72]/95 backdrop-blur-sm',
        metadataBg: 'bg-white/10 backdrop-blur-md',
        divider: 'bg-gradient-to-r from-white/80 to-blue-200/80'
      },
      blue: {
        background: 'bg-gradient-to-br from-[#3b82f6] via-[#2563eb] to-[#1d4ed8]',
        cardBg: 'bg-white/10 backdrop-blur-md',
        text: 'text-white',
        textSecondary: 'text-white/90',
        textTertiary: 'text-white/70',
        border: 'border-white/20',
        accent: 'bg-white',
        accentText: 'text-white',
        accentGradient: 'from-[#3b82f6] to-[#2563eb]',
        badge: 'bg-white/20 backdrop-blur-md text-white border-2 border-white/30',
        hover: 'hover:bg-white/15',
        shadow: 'shadow-2xl shadow-blue-900/50',
        overlay: 'bg-gradient-to-br from-[#3b82f6]/95 via-[#2563eb]/95 to-[#1d4ed8]/95 backdrop-blur-sm',
        metadataBg: 'bg-white/10 backdrop-blur-md',
        divider: 'bg-gradient-to-r from-white/80 to-blue-200/80'
      }
    };
    return themes[config.theme || 'dark'];
  };

  const theme = getThemeStyles();

  // Loading state
  if (loading) {
    return (
      <div className={`w-full h-full flex items-center justify-center ${theme.background}`}>
        <div className="text-center" style={{ padding: `${48 * m.spacing}px` }}>
          <Rss 
            className={`animate-pulse mx-auto mb-4 ${theme.text}`} 
            size={48 * m.font} 
          />
          <p 
            className={theme.textSecondary}
            style={{ fontSize: `${20 * m.font}px` }}
          >
            {t('rss.loading')}
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !feedData) {
    return (
      <div className={`w-full h-full flex items-center justify-center ${theme.background}`}>
        <div className="text-center" style={{ padding: `${48 * m.spacing}px` }}>
          <AlertCircle 
            className="text-red-500 mx-auto mb-4" 
            size={48 * m.font} 
          />
          <p 
            className={`font-semibold ${theme.text} mb-2`}
            style={{ fontSize: `${24 * m.font}px` }}
          >
            {t('rss.errorLoading')}
          </p>
          <p 
            className={theme.textSecondary}
            style={{ fontSize: `${16 * m.font}px` }}
          >
            {error}
          </p>
        </div>
      </div>
    );
  }

  // No items
  if (!feedData.items || feedData.items.length === 0) {
    return (
      <div className={`w-full h-full flex items-center justify-center ${theme.background}`}>
        <div className="text-center" style={{ padding: `${48 * m.spacing}px` }}>
          <Rss 
            className={`${theme.text} mx-auto mb-4`} 
            size={48 * m.font} 
          />
          <p 
            className={`font-semibold ${theme.text}`}
            style={{ fontSize: `${24 * m.font}px` }}
          >
            {t('rss.noItems')}
          </p>
        </div>
      </div>
    );
  }

  // CARD LAYOUT - ✅ RESPONSIVE + FIXED IMAGE DISPLAY
  if (config.layout === 'card') {
    return (
      <div className={`w-full h-full ${theme.background} overflow-hidden`}>
        <div className="h-full flex flex-col" style={{ padding: `${32 * m.spacing}px` }}>
          
          {/* Header */}
          <div style={{ marginBottom: `${24 * m.spacing}px` }}>
            <div className="flex items-center gap-4 mb-2">
              <Rss 
                className={theme.accentText} 
                size={48 * m.font} 
              />
              <h2 
                className={`font-bold ${theme.text}`}
                style={{ fontSize: `${40 * m.font}px` }}
              >
                {feedData.feedTitle}
              </h2>
            </div>
            <p 
              className={theme.textTertiary} 
              style={{ 
                fontSize: `${18 * m.font}px`,
                marginLeft: `${(48 * m.font) + 16}px`
              }}
            >
              {feedData.items.length} {t('rss.articles')}
            </p>
          </div>

          {/* Cards Grid */}
          <div className="flex-1 overflow-y-auto pr-2 scrollbar-thin">
            <div 
              className="grid gap-6 pb-6"
              style={{
                gridTemplateColumns: `repeat(auto-fill, minmax(${350 * m.card}px, 1fr))`,
                gap: `${24 * m.spacing}px`
              }}
            >
              {feedData.items.map((item, index) => (
                <div
                  key={index}
                  className={`${theme.cardBg} rounded-2xl overflow-hidden ${theme.shadow} ${theme.border} border-2 transition-all duration-300 hover:scale-105 flex flex-col group`}
                >
                  {/* Image - FIXED VERSION */}
                  {config.showImages && item.imageUrl && (
                    <div style={{ padding: `${16 * m.spacing}px` }}>
                      <div className={`relative rounded-xl overflow-hidden ${theme.shadow} ${theme.border} border-2`}>
                        <div style={{ height: `${224 * m.card}px` }} className="relative overflow-hidden bg-gray-900/50">
                          
                          {/* Background Blur Layer */}
                          <div className="absolute inset-0 z-0">
                            <img
                              src={item.imageUrl}
                              alt=""
                              className="w-full h-full object-cover blur-3xl opacity-40 scale-110"
                            />
                          </div>
                          
                          {/* Sharp Image Layer */}
                          <div className="relative w-full h-full flex items-center justify-center z-10" style={{ padding: `${8 * m.spacing}px` }}>
                            <img
                              src={item.imageUrl}
                              alt={item.title}
                              className="max-w-full max-h-full object-contain drop-shadow-2xl"
                              onError={(e) => e.target.style.display = 'none'}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Content */}
                  <div 
                    className="flex-1 flex flex-col" 
                    style={{ 
                      padding: `0 ${24 * m.spacing}px ${24 * m.spacing}px ${24 * m.spacing}px` 
                    }}
                  >
                    <h3 
                      className={`font-bold ${theme.text} line-clamp-2 leading-tight`}
                      style={{ 
                        fontSize: `${20 * m.font}px`,
                        marginBottom: `${12 * m.spacing}px`
                      }}
                    >
                      {item.title}
                    </h3>

                    {config.showDescription && item.description && (
                      <p 
                        className={`${theme.textSecondary} leading-relaxed flex-1`}
                        style={{ 
                          fontSize: `${14 * m.font}px`,
                          marginBottom: `${16 * m.spacing}px`
                        }}
                      >
                        {item.description.replace(/<[^>]*>/g, '')}
                      </p>
                    )}

                    <div 
                      className={`h-1 ${theme.divider} rounded-full`}
                      style={{ 
                        width: `${96 * m.spacing}px`,
                        marginBottom: `${16 * m.spacing}px`
                      }}
                    ></div>

                    <div className={`flex items-center justify-between gap-3 ${theme.metadataBg} rounded-full`}
                      style={{ padding: `${12 * m.spacing}px ${16 * m.spacing}px` }}
                    >
                      {config.showDate && (
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Calendar size={18 * m.font} className={theme.accentText} />
                          <span 
                            className={`${theme.text} font-medium`}
                            style={{ fontSize: `${14 * m.font}px` }}
                          >
                            {formatDate(item.pubDate)}
                          </span>
                        </div>
                      )}
                      
                      {item.author && (
                        <div className="flex items-center gap-2 min-w-0">
                          <User size={18 * m.font} className={theme.accentText} />
                          <span 
                            className={`${theme.text} font-medium truncate`}
                            style={{ fontSize: `${14 * m.font}px` }}
                          >
                            {item.author}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <style dangerouslySetInnerHTML={{__html: `
          .scrollbar-thin::-webkit-scrollbar {
            width: ${8 * m.spacing}px;
          }
          .scrollbar-thin::-webkit-scrollbar-track {
            background: transparent;
          }
          .scrollbar-thin::-webkit-scrollbar-thumb {
            background: rgba(156, 163, 175, 0.3);
            border-radius: 4px;
          }
          .scrollbar-thin::-webkit-scrollbar-thumb:hover {
            background: rgba(156, 163, 175, 0.5);
          }
        `}} />
      </div>
    );
  }

  // TICKER LAYOUT - ✅ RESPONSIVE
  if (config.layout === 'ticker') {
    return (
      <div className={`w-full h-full ${theme.background} overflow-hidden flex items-end`}>
        <div className={`w-full ${theme.cardBg} border-t-4 ${theme.border} ${theme.shadow}`}>
          
          <div 
            className={`flex items-center justify-between border-b-2 ${theme.border}`}
            style={{ padding: `${16 * m.spacing}px ${32 * m.spacing}px` }}
          >
            <div className="flex items-center gap-3">
              <div className={`rounded-xl ${theme.badge}`}
                style={{ padding: `${8 * m.spacing}px` }}
              >
                <Rss size={24 * m.font} />
              </div>
              <div>
                <h3 
                  className={`font-bold ${theme.text}`}
                  style={{ fontSize: `${20 * m.font}px` }}
                >
                  {feedData.feedTitle}
                </h3>
                <p 
                  className={theme.textTertiary}
                  style={{ fontSize: `${12 * m.font}px` }}
                >
                  Live-Updates
                </p>
              </div>
            </div>

            <div 
              className={`flex items-center gap-2 ${theme.metadataBg} rounded-full`}
              style={{ padding: `${8 * m.spacing}px ${16 * m.spacing}px` }}
            >
              <div 
                className="rounded-full animate-pulse"
                style={{ 
                  width: `${8 * m.spacing}px`,
                  height: `${8 * m.spacing}px`,
                  backgroundColor: '#ef4444'
                }}
              ></div>
              <span 
                className={`font-bold ${theme.text}`}
                style={{ fontSize: `${14 * m.font}px` }}
              >
                LIVE
              </span>
            </div>
          </div>

          <div 
            className="relative overflow-hidden"
            style={{ padding: `${16 * m.spacing}px 0` }}
          >
            <div 
              className="ticker-scroll flex items-center"
              style={{ 
                gap: `${48 * m.spacing}px`,
                paddingLeft: `${32 * m.spacing}px`,
                paddingRight: `${32 * m.spacing}px`
              }}
            >
              {[...feedData.items, ...feedData.items, ...feedData.items].map((item, index) => (
                <div
                  key={index}
                  className="flex items-center flex-shrink-0 group"
                  style={{ gap: `${24 * m.spacing}px` }}
                >
                  {config.showImages && item.imageUrl && (
                    <div className="relative">
                      <div className={`absolute inset-0 rounded-lg bg-gradient-to-r ${theme.accentGradient} opacity-30 blur-lg`}></div>
                      
                      <div 
                        className={`relative rounded-lg overflow-hidden ${theme.border} border-2 ${theme.shadow}`}
                        style={{
                          width: `${128 * m.card}px`,
                          height: `${80 * m.card}px`
                        }}
                      >
                        <img
                          src={item.imageUrl}
                          alt={item.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      </div>
                    </div>
                  )}

                  <div className="flex items-center" style={{ gap: `${16 * m.spacing}px` }}>
                    <div 
                      className={`rounded-full bg-gradient-to-r ${theme.accentGradient} flex-shrink-0`}
                      style={{
                        width: `${8 * m.spacing}px`,
                        height: `${8 * m.spacing}px`
                      }}
                    ></div>

                    <h4 
                      className={`font-bold ${theme.text} line-clamp-2 leading-tight`}
                      style={{ 
                        fontSize: `${18 * m.font}px`,
                        maxWidth: `${512 * m.card}px`
                      }}
                    >
                      {item.title}
                    </h4>

                    <div 
                      className={`${theme.border} bg-gradient-to-b ${theme.accentGradient} flex-shrink-0`}
                      style={{
                        width: '1px',
                        height: `${48 * m.spacing}px`
                      }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <style dangerouslySetInnerHTML={{__html: `
          @keyframes ticker-scroll {
            0% {
              transform: translateX(0);
            }
            100% {
              transform: translateX(-33.333%);
            }
          }

          .ticker-scroll {
            animation: ticker-scroll 60s linear infinite;
            will-change: transform;
          }

          .ticker-scroll:hover {
            animation-play-state: paused;
          }
        `}} />
      </div>
    );
  }

  // FULLSCREEN LAYOUT - ✅ RESPONSIVE  
  if (config.layout === 'fullscreen') {
    const currentItem = feedData.items[currentIndex];

    return (
      <div className={`w-full h-full relative overflow-hidden ${theme.background}`}>
        
        {config.showImages && currentItem.imageUrl && config.theme !== 'light' && (
          <div className="absolute inset-0">
            <img
              src={currentItem.imageUrl}
              alt=""
              className="w-full h-full object-cover blur-3xl opacity-30 scale-110"
              onError={(e) => e.target.style.display = 'none'}
            />
            <div className={`absolute inset-0 ${theme.overlay}`}></div>
          </div>
        )}

        <div className="relative z-10 flex h-full">
          
          {config.showImages && currentItem.imageUrl && (
            <div 
              className="flex items-center justify-center"
              style={{ 
                width: '45%',
                padding: `${48 * m.spacing}px`
              }}
            >
              <div className="relative max-w-full max-h-full animate-fade-in">
                
                <div className={`absolute inset-0 rounded-2xl bg-gradient-to-r ${theme.accentGradient} opacity-20 blur-2xl scale-105`}></div>
                
                <div className={`relative rounded-2xl overflow-hidden ${theme.shadow} ${theme.border} border-2`}>
                  <img
                    src={currentItem.imageUrl}
                    alt={currentItem.title}
                    style={{ maxHeight: '85vh' }}
                    className="max-w-full object-contain bg-gray-900"
                    onError={(e) => e.target.parentElement.parentElement.style.display = 'none'}
                  />
                </div>
              </div>
            </div>
          )}

          <div 
            className="flex flex-col justify-center"
            style={{ 
              width: config.showImages && currentItem.imageUrl ? '55%' : '100%',
              padding: `${64 * m.spacing}px ${48 * m.spacing}px`
            }}
          >
            
            <div style={{ marginBottom: `${32 * m.spacing}px` }} className="animate-fade-in">
              <div 
                className={`inline-flex items-center ${theme.badge} rounded-2xl ${theme.shadow}`}
                style={{ 
                  gap: `${16 * m.spacing}px`,
                  padding: `${16 * m.spacing}px ${32 * m.spacing}px`
                }}
              >
                <Rss size={32 * m.font} className="flex-shrink-0" />
                <span 
                  className="font-bold tracking-wide"
                  style={{ fontSize: `${24 * m.font}px` }}
                >
                  {feedData.feedTitle}
                </span>
              </div>
            </div>

            <h1 
              className={`font-black ${theme.text} leading-[1.1] drop-shadow-2xl animate-slide-up`}
              style={{ 
                fontSize: `${70 * m.font}px`,
                marginBottom: `${32 * m.spacing}px`
              }}
            >
              {currentItem.title}
            </h1>

            {config.showDescription && currentItem.description && (
              <div style={{ marginBottom: `${32 * m.spacing}px` }} className="animate-fade-in-delayed">
                <p 
                  className={`${theme.textSecondary} leading-relaxed font-light`}
                  style={{ fontSize: `${30 * m.font}px` }}
                >
                  {truncate(currentItem.description, 320)}
                </p>
              </div>
            )}

            <div 
              className={`h-1 ${theme.divider} rounded-full shadow-lg`}
              style={{ 
                width: `${128 * m.spacing}px`,
                marginBottom: `${32 * m.spacing}px`
              }}
            ></div>

            <div 
              className="flex flex-wrap items-center"
              style={{ 
                gap: `${16 * m.spacing}px`,
                marginBottom: `${48 * m.spacing}px`,
                fontSize: `${18 * m.font}px`
              }}
            >
              {config.showDate && (
                <div 
                  className={`flex items-center ${theme.metadataBg} rounded-full transition-all hover:scale-105`}
                  style={{ 
                    gap: `${8 * m.spacing}px`,
                    padding: `${12 * m.spacing}px ${20 * m.spacing}px`
                  }}
                >
                  <Calendar size={22 * m.font} className={theme.accentText} />
                  <span className={`${theme.textSecondary} font-medium`}>
                    {formatDate(currentItem.pubDate)}
                  </span>
                </div>
              )}
              {currentItem.author && (
                <div 
                  className={`flex items-center ${theme.metadataBg} rounded-full transition-all hover:scale-105`}
                  style={{ 
                    gap: `${8 * m.spacing}px`,
                    padding: `${12 * m.spacing}px ${20 * m.spacing}px`
                  }}
                >
                  <User size={22 * m.font} className={theme.accentText} />
                  <span className={`${theme.textSecondary} font-medium`}>
                    {currentItem.author.length > 60 ? truncate(currentItem.author, 60) : currentItem.author}
                  </span>
                </div>
              )}
            </div>

            <div 
              className="flex items-center animate-fade-in-delayed"
              style={{ gap: `${12 * m.spacing}px` }}
            >
              <div style={{ display: 'flex', gap: `${8 * m.spacing}px` }}>
                {feedData.items.map((_, index) => (
                  <div
                    key={index}
                    className={`rounded-full transition-all duration-500 ${
                      index === currentIndex 
                        ? `bg-gradient-to-r ${theme.accentGradient} ${theme.shadow} scale-110` 
                        : `${theme.metadataBg} hover:scale-125 cursor-pointer`
                    }`}
                    style={{
                      width: index === currentIndex ? `${48 * m.spacing}px` : `${12 * m.spacing}px`,
                      height: `${12 * m.spacing}px`
                    }}
                  />
                ))}
              </div>
              <div 
                className="flex items-center"
                style={{ 
                  gap: `${8 * m.spacing}px`,
                  marginLeft: `${8 * m.spacing}px`
                }}
              >
                <div 
                  className={theme.border}
                  style={{ 
                    width: '1px',
                    height: `${24 * m.spacing}px`
                  }}
                ></div>
                <span 
                  className={`${theme.textTertiary} font-semibold tabular-nums`}
                  style={{ fontSize: `${20 * m.font}px` }}
                >
                  {currentIndex + 1}
                </span>
                <span 
                  className={`${theme.textTertiary} font-light`}
                  style={{ fontSize: `${18 * m.font}px` }}
                >
                  /
                </span>
                <span 
                  className={`${theme.textTertiary} font-light tabular-nums`}
                  style={{ fontSize: `${18 * m.font}px` }}
                >
                  {feedData.items.length}
                </span>
              </div>
            </div>
          </div>
        </div>

        <style dangerouslySetInnerHTML={{__html: `
          @keyframes fade-in {
            from { 
              opacity: 0;
              transform: scale(0.95);
            }
            to { 
              opacity: 1;
              transform: scale(1);
            }
          }
          
          @keyframes slide-up {
            from { 
              opacity: 0;
              transform: translateY(30px);
            }
            to { 
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes fade-in-delayed {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          
          .animate-fade-in {
            animation: fade-in 0.8s ease-out;
          }
          
          .animate-slide-up {
            animation: slide-up 0.8s ease-out;
          }
          
          .animate-fade-in-delayed {
            animation: fade-in-delayed 1s ease-out 0.4s both;
          }
        `}} />
      </div>
    );
  }

  return null;
};

export default RSSWidget;